// A form_input form shown in the chat window sends its answers back as an
// ordinary /qna turn. The backend recognises that turn and hands the answers to
// the agent's form_input step instead of asking again, so this text is a
// contract: SUBMITTED_FORM_PREFIX, ORIGINAL_REQUEST_PREFIX and the
// "- label: value" line format must stay identical to
// chat__pdf/server/Tools/form_input_parsing.py.
export const SUBMITTED_FORM_PREFIX = 'Submitted form details:';

// suspended_scripts.suspend_event_type for a run parked on the user supplying data,
// as opposed to the default 'quvya_wait_event' (released by a plain approval). Must
// match SuspendEventType.USER_INPUT in chat__pdf/server/constant.py. The task tab
// renders a suspension's required_input as a form only for this event type.
export const SUSPEND_EVENT_TYPE_USER_INPUT = 'user_input';

// required_input.type produced by the form_input tool.
export const REQUIRED_INPUT_TYPE_FORM = 'form_input';

// The request that triggered the form, appended AFTER the answers so the
// submission still *starts* with SUBMITTED_FORM_PREFIX (the backend's form-loop
// guard checks the leading text). Carrying it forward is what stops the agent
// router and a generated classifier step from re-deciding the workflow purely
// from the answers block.
export const ORIGINAL_REQUEST_PREFIX = 'Original request:';

/**
 * Compose submitted form answers into the chat message the backend parses.
 * @param {Record<string, string|number>} answers keyed by field label
 * @param {string} [originalQuery] the request that triggered the form. Optional -
 *   when absent or blank the output is exactly the answers block, which is what
 *   the messaging interfaces (Telegram/Teams) produce.
 * @returns {string}
 */
export const buildSubmittedFormText = (answers, originalQuery) => {
    const lines = Object.entries(answers || {}).map(
        ([label, value]) => `- ${label}: ${value}`
    );
    const answersText = `${SUBMITTED_FORM_PREFIX}\n${lines.join('\n')}`;

    const original =
        typeof originalQuery === 'string' ? originalQuery.trim() : '';
    if (!original) return answersText;

    return `${answersText}\n\n${ORIGINAL_REQUEST_PREFIX} ${original}`;
};

/**
 * Recover the request that buildSubmittedFormText appended, if any.
 * Mirrors extract_original_request() in
 * chat__pdf/server/Tools/form_input_parsing.py.
 * @param {string} [text]
 * @returns {string|null}
 */
export const extractOriginalRequest = (text) => {
    if (!text) return null;
    const lines = text.split('\n');
    const idx = lines.findIndex((line) =>
        line.trimStart().startsWith(ORIGINAL_REQUEST_PREFIX)
    );
    if (idx === -1) return null;

    const first = lines[idx]
        .trimStart()
        .slice(ORIGINAL_REQUEST_PREFIX.length)
        .trimStart();
    const original = [first, ...lines.slice(idx + 1)].join('\n').trim();
    return original || null;
};
